// src/pages/SpendingAnalysisPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { formatCurrency } from '../utils/formatting';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import LoadingSpinner from '../components/LoadingSpinner';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const SpendingAnalysisPage = () => {
    const { loading, discretionarySpending, runwayData } = useAppContext();
    const [selectedReduction, setSelectedReduction] = useState(null);

    if (loading || !discretionarySpending) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner size="large" message="Loading spending data..." />
            </div>
        );
    }

    // Prepare data for the chart
    const chartData = {
        labels: discretionarySpending.discretionary_spending.map(item => item.month),
        datasets: [
            {
                label: 'Discretionary Spending',
                data: discretionarySpending.discretionary_spending.map(item => item.amount),
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
                borderColor: 'rgb(53, 162, 235)',
                borderWidth: 1,
            },
            {
                label: 'Safe Monthly Limit',
                data: Array(discretionarySpending.discretionary_spending.length).fill(
                    discretionarySpending.suggested_monthly_limit
                ),
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                borderColor: 'rgb(34, 197, 94)',
                borderWidth: 2,
                type: 'line',
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatCurrency(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return formatCurrency(value, false);
                    }
                }
            }
        }
    };

    const handleReductionClick = (reduction) => {
        setSelectedReduction(reduction);
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Discretionary Spending Analysis</h1>
                <Link
                    to="/"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                    Back to Dashboard
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Monthly Discretionary Spending</h2>
                <div className="h-80 w-full">
                    <Bar data={chartData} options={chartOptions} />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-blue-50 p-3 rounded-md">
                        <div className="text-sm text-blue-800">Average Monthly</div>
                        <div className="text-2xl font-bold">{formatCurrency(discretionarySpending.average_monthly)}</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-md">
                        <div className="text-sm text-green-800">Suggested Monthly Limit</div>
                        <div className="text-2xl font-bold">{formatCurrency(discretionarySpending.suggested_monthly_limit)}</div>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-md">
                        <div className="text-sm text-purple-800">Months Remaining</div>
                        <div className="text-2xl font-bold">{discretionarySpending.months_remaining}</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4">Impact of Spending Reduction</h2>
                    <p className="text-gray-600 mb-4">
                        See how reducing your monthly discretionary spending can impact your final balance:
                    </p>

                    <div className="grid grid-cols-1 gap-3">
                        {discretionarySpending.potential_scenarios.map((scenario) => (
                            <button
                                key={scenario.monthly_reduction}
                                onClick={() => handleReductionClick(scenario)}
                                className={`p-3 border rounded-md text-left hover:bg-gray-50 ${selectedReduction?.monthly_reduction === scenario.monthly_reduction
                                        ? 'border-blue-500 ring-2 ring-blue-200'
                                        : 'border-gray-200'
                                    }`}
                            >
                                <div className="flex justify-between">
                                    <span>Reduce by {formatCurrency(scenario.monthly_reduction)}/month</span>
                                    <span className="font-semibold text-green-600">+{formatCurrency(scenario.total_saved)}</span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                    New projected balance: {formatCurrency(scenario.new_projected_balance)}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4">Spending Recommendations</h2>

                    {selectedReduction ? (
                        <div className="bg-blue-50 p-4 rounded-md">
                            <h3 className="font-semibold text-blue-800">Reducing by {formatCurrency(selectedReduction.monthly_reduction)}/month</h3>
                            <p className="mt-2">
                                If you reduce your discretionary spending by {formatCurrency(selectedReduction.monthly_reduction)} per month,
                                you'll save {formatCurrency(selectedReduction.total_saved)} by the end of your residency.
                            </p>
                            <p className="mt-2">
                                Your projected final balance would be {formatCurrency(selectedReduction.new_projected_balance)}, which is
                                {selectedReduction.new_projected_balance >= runwayData?.target_amount
                                    ? ' above your target amount.'
                                    : selectedReduction.new_projected_balance >= runwayData?.minimum_threshold
                                        ? ' above your minimum threshold but below your target.'
                                        : ' still below your minimum threshold.'}
                            </p>

                            <div className="mt-3 p-3 bg-white rounded-md">
                                <h4 className="font-semibold">Some ways to achieve this:</h4>
                                <ul className="list-disc list-inside mt-2">
                                    <li>Reduce dining out by {formatCurrency(selectedReduction.monthly_reduction / 4)} per month</li>
                                    <li>Cut back on subscription services you rarely use</li>
                                    <li>Plan fun activities that cost less</li>
                                    <li>Use fun money allocation more strategically</li>
                                </ul>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-gray-600">
                                Your current average monthly discretionary spending is {formatCurrency(discretionarySpending.average_monthly)}.
                            </p>
                            <p className="text-gray-600 mt-2">
                                Based on your financial goals, we recommend limiting this to {formatCurrency(discretionarySpending.suggested_monthly_limit)} per month.
                            </p>
                            <p className="mt-4 font-medium">
                                Select a spending reduction scenario on the left to see detailed recommendations.
                            </p>
                        </div>
                    )}

                    <div className="mt-6">
                        <h3 className="font-semibold mb-2">Fun Money Strategy</h3>
                        <p className="text-gray-600">
                            Using your current fun money allocation of {formatCurrency(runwayData?.safe_monthly_discretionary / 2)} per person per month
                            can help you stay within your safe discretionary spending limit.
                        </p>
                        <div className="mt-4">
                            <Link
                                to="/fun-money"
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-block"
                            >
                                Manage Fun Money
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpendingAnalysisPage;